import {FC} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import {Alert, Stack} from "@mui/material";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {useBulkCompleteLessons} from "@/app2/shared/data";
import {palette} from "@/constants";

interface BulkCompleteModalProps {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
	userId: number;
	lessonEnrollmentIds: number[];
}

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

/**
 * Modal for bulk completing lessons for a user
 */
export const BulkCompleteModal: FC<BulkCompleteModalProps> = ({
	open,
	onClose,
	onSuccess,
	userId,
	lessonEnrollmentIds
}) => {
	const {t} = useSSTranslation();
	const {successSnackbar, errorSnackbar, warningSnackbar} = useCustomSnackbar();
	const {mutate: bulkComplete, isPending, error, reset} = useBulkCompleteLessons();

	const handleConfirm = () => {
		const today = new Date().toISOString().split("T")[0];
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		bulkComplete(
			{userId, lessonEnrollmentIds, date: today, timezone},
			{
				onSuccess: (result) => {
					const completedCount = result.completedIds?.length ?? 0;
					const errorCount = result.errors?.length ?? 0;

					if (errorCount === 0) {
						successSnackbar(
							t("admin_bulk_complete_lessons_success", "Successfully marked {{count}} lesson(s) as complete", {
								count: completedCount
							})
						);
						onSuccess();
					} else if (completedCount === 0) {
						errorSnackbar(
							`${t("admin_bulk_complete_lessons_all_failed", "Could not mark lessons complete.")} ${result.errors.join("; ")}`
						);
					} else {
						warningSnackbar(
							`${t("admin_bulk_complete_lessons_partial", "Marked {{completed}} lesson(s) complete; {{failed}} failed.", {
								completed: completedCount,
								failed: errorCount
							})} ${result.errors.join("; ")}`
						);
					}
				},
				onError: (err: Error) => {
					errorSnackbar(
						t("admin_bulk_complete_lessons_error", "Failed to mark lessons complete: {{message}}", {
							message: err.message
						})
					);
				}
			}
		);
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (reason !== "backdropClick") {
					handleClose();
				}
			}}
			maxWidth="sm"
			fullWidth
			aria-labelledby="bulk-complete-dialog-title"
			aria-describedby="bulk-complete-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
		>
			<DialogTitle id="bulk-complete-dialog-title">Mark Lessons as Complete</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<DialogContentText id="bulk-complete-dialog-description" variant="body1" color="text.primary">
						Are you sure you want to mark {lessonEnrollmentIds.length} lesson
						{lessonEnrollmentIds.length !== 1 ? "s" : ""} as complete?
					</DialogContentText>
					<DialogContentText variant="body2" color="text.primary">
						This action cannot be undone.
					</DialogContentText>
					{error && (
						<Alert severity="error">
							{error.message}
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={handleClose} sx={buttonSx} disabled={isPending}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={handleConfirm}
					sx={buttonSx}
					disabled={isPending}
					startIcon={isPending ? <CircularProgress size={16} /> : undefined}
				>
					{isPending ? t("common_completing", "Completing...") : t("common_confirm", "Confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
